import { uiText } from './i18n/uiText';
import React from 'react';
import { TranslationProvider } from './i18n';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/AuthContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AuditsPage from './pages/AuditsPage';
import AuditDetailPage from './pages/AuditDetailPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateEditorPage from './pages/TemplateEditorPage';
import UsersPage from './pages/UsersPage';
import IssuesPage from './pages/IssuesPage';
import CorrectiveActionsPage from './pages/CorrectiveActionsPage';
import SearchPage from './pages/SearchPage';
import AdminAuditTrailPage from './pages/AdminAuditTrailPage';
import SystemControlPage from './pages/SystemControlPage';
import SecurityPage from './pages/SecurityPage';
import AuditPlansPage from './pages/AuditPlansPage';
import AuditProgramsPage from './pages/AuditProgramsPage';
import AuditReportsPage from './pages/AuditReportsPage';
import CompaniesPage from './pages/CompaniesPage';
import ContractsPage from './pages/ContractsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SelfCheckPage from './pages/SelfCheckPage';
import SelfCheckReviewPage from './pages/SelfCheckReviewPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import SelfCheckWeightsPage from './pages/SelfCheckWeightsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CorrectivePlansPage from './pages/CorrectivePlansPage';
import OutgoingEmailsPage from './pages/OutgoingEmailsPage';
import AuditorPoolPage from './pages/AuditorPoolPage';
import AuditCalendarPage from './pages/AuditCalendarPage';
import AuditorAssignmentNotificationsPage from './pages/AuditorAssignmentNotificationsPage';
import AuditorSchedulePage from './pages/AuditorSchedulePage';
import ManagementPage from './pages/ManagementPage';
import NotificationsPage from './pages/NotificationsPage';
import LocalAiAssistantPage from './pages/LocalAiAssistantPage';
import HelpPage from './pages/HelpPage';
import MyOrganizationPage from './pages/MyOrganizationPage';
import QualificationPage from './pages/QualificationPage';
import MyQualificationPage from './pages/MyQualificationPage';
import SupplierCompanyPage from './pages/SupplierCompanyPage';
import SupplierSettingsPage from './pages/SupplierSettingsPage';
import ClassifierPage from './pages/ClassifierPage';
import AuditPreparationPage from './pages/AuditPreparationPage';
import RequirementsLibraryPage from './pages/RequirementsLibraryPage';
import SupplierNeedsPage from './pages/SupplierNeedsPage';
import { ROLES, hasRole, isAuditeeRole, rolesForResource } from './utils/permissions';
import { isPublicAuthRoute } from './utils/publicAuthRoute';

const DASHBOARD_ROLES = rolesForResource('dashboard');
const AUDIT_ROLES = rolesForResource('audits');
const NOTIFICATION_ROLES = rolesForResource('notifications');
const AUDIT_CREATE_ROLES = rolesForResource('audits', 'create');
const MANAGEMENT_ROLES = rolesForResource('management');
const ADMIN_ROLES = rolesForResource('admin');
const SELF_CHECK_ROLES = rolesForResource('self_check');
const LOCAL_AI_ROLES = [ROLES.ADMIN, ROLES.AUDITOR];
const AUDITOR_POOL_ROLES = rolesForResource('auditor_pool');
const COMPANY_SUMMARY_ROLES = rolesForResource('executive_dashboard');
const ERP_FLAG_ROLES = rolesForResource('erp_flags');
const ACCOUNT_ROLES = Object.values(ROLES);

function RouterEventBridge() {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const handleAuthRequired = () => {
      if (!isPublicAuthRoute(location.pathname)) navigate('/login', { replace: true });
    };

    window.addEventListener('app:auth-required', handleAuthRequired);
    return () => {
      window.removeEventListener('app:auth-required', handleAuthRequired);
    };
  }, [location.pathname, navigate]);

  return null;
}

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="app-bootstrap-loading" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <span>{uiText("Проверяем доступ…")}</span>
      </div>
    );
  }
  const isAuthorized = Boolean(user) && hasRole(user, roles);
  if (!user) return <Navigate to="/login" replace />;
  // Если у пользователя стоит флаг «сменить пароль при первом входе» —
  // редиректим на страницу смены пароля. Только сама эта страница
  // и /logout доступны до тех пор, пока пароль не сменён.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  if (!isAuthorized) {
    if (isAuditeeRole(user)) return <Navigate to="/dashboard" replace />;
    return <Navigate to={user.role === ROLES.ANALYST ? '/company-summary' : '/dashboard'} replace />;
  }
  // Route-local state must not carry an old account's rows into a new session.
  return <React.Fragment key={`${user.id}:${user.role}`}>{children}</React.Fragment>;
}

function DefaultHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (isAuditeeRole(user)) return <Navigate to="/dashboard" replace />;
  if (user.role === ROLES.ANALYST) return <Navigate to="/company-summary" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const routerBaseName = process.env.REACT_APP_BASENAME || process.env.PUBLIC_URL || undefined;
  return (
    <TranslationProvider><AuthProvider>
      <ErrorBoundary>
        <BrowserRouter basename={routerBaseName}>
          <RouterEventBridge />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<DefaultHome />} />
              <Route path="dashboard" element={<PrivateRoute roles={DASHBOARD_ROLES}><DashboardPage /></PrivateRoute>} />
              <Route path="my-work" element={<PrivateRoute roles={DASHBOARD_ROLES}><Navigate to="/dashboard" replace /></PrivateRoute>} />
              <Route path="audits" element={<PrivateRoute roles={AUDIT_ROLES}><AuditsPage /></PrivateRoute>} />
              <Route path="audits/new" element={<PrivateRoute roles={AUDIT_CREATE_ROLES}><AuditsPage /></PrivateRoute>} />
              <Route path="audits/:id" element={<PrivateRoute roles={AUDIT_ROLES}><AuditDetailPage /></PrivateRoute>} />
              <Route path="audit-calendar" element={<PrivateRoute roles={AUDIT_ROLES}><AuditCalendarPage /></PrivateRoute>} />
              <Route path="issues" element={<PrivateRoute roles={AUDIT_ROLES}><IssuesPage /></PrivateRoute>} />
              <Route path="corrective-actions" element={<PrivateRoute roles={AUDIT_ROLES}><CorrectiveActionsPage /></PrivateRoute>} />
              <Route path="search" element={<PrivateRoute roles={AUDIT_ROLES}><SearchPage /></PrivateRoute>} />
              <Route path="notifications" element={<PrivateRoute roles={NOTIFICATION_ROLES}><NotificationsPage /></PrivateRoute>} />
              <Route path="security" element={<PrivateRoute roles={ACCOUNT_ROLES}><SecurityPage /></PrivateRoute>} />

              <Route path="ai-assistant" element={<PrivateRoute roles={LOCAL_AI_ROLES}><LocalAiAssistantPage /></PrivateRoute>} />
              <Route path="analytics/assistant" element={<Navigate to="/ai-assistant" replace />} />
              <Route path="analytics/*" element={<DefaultHome />} />

              <Route path="management" element={<PrivateRoute roles={MANAGEMENT_ROLES}><ManagementPage /></PrivateRoute>} />
              <Route path="templates" element={<PrivateRoute roles={MANAGEMENT_ROLES}><TemplatesPage /></PrivateRoute>} />
              <Route path="templates/new" element={<PrivateRoute roles={MANAGEMENT_ROLES}><TemplateEditorPage /></PrivateRoute>} />
              <Route path="templates/:id" element={<PrivateRoute roles={MANAGEMENT_ROLES}><TemplateEditorPage /></PrivateRoute>} />
              <Route path="companies" element={<PrivateRoute roles={MANAGEMENT_ROLES}><CompaniesPage /></PrivateRoute>} />
              {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Route path="suppliers/:companyId" element={<PrivateRoute roles={[...MANAGEMENT_ROLES, ROLES.AUDITEE]}><SupplierCompanyPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Route path="supplier-settings" element={<PrivateRoute roles={MANAGEMENT_ROLES}><SupplierSettingsPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' && <Route path="classifier" element={<PrivateRoute roles={MANAGEMENT_ROLES}><ClassifierPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' && <Route path="requirements" element={<PrivateRoute roles={MANAGEMENT_ROLES}><RequirementsLibraryPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' && <Route path="audit-preparation" element={<PrivateRoute roles={MANAGEMENT_ROLES}><AuditPreparationPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Route path="supplier-needs" element={<PrivateRoute roles={MANAGEMENT_ROLES}><SupplierNeedsPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Route path="qualification" element={<PrivateRoute roles={MANAGEMENT_ROLES}><QualificationPage /></PrivateRoute>} />}
              {process.env.REACT_APP_ENABLE_QUALIFICATION === 'true' && <Route path="my-qualification" element={<PrivateRoute roles={[ROLES.AUDITEE]}><MyQualificationPage /></PrivateRoute>} />}
              <Route path="contracts" element={<PrivateRoute roles={MANAGEMENT_ROLES}><ContractsPage /></PrivateRoute>} />
              <Route path="signatories" element={<PrivateRoute roles={MANAGEMENT_ROLES}><Navigate to="/companies" replace /></PrivateRoute>} />
              <Route path="self-check/review/:companyId" element={<PrivateRoute roles={MANAGEMENT_ROLES}><SelfCheckReviewPage /></PrivateRoute>} />
              <Route path="self-check" element={<PrivateRoute roles={SELF_CHECK_ROLES}><SelfCheckPage /></PrivateRoute>} />
              <Route path="my-organization" element={<PrivateRoute roles={[ROLES.AUDITEE]}><MyOrganizationPage /></PrivateRoute>} />
              <Route path="company-summary" element={<PrivateRoute roles={COMPANY_SUMMARY_ROLES}><ExecutiveDashboardPage /></PrivateRoute>} />
              <Route path="executive" element={<PrivateRoute roles={COMPANY_SUMMARY_ROLES}><Navigate to="/company-summary" replace /></PrivateRoute>} />
              <Route path="erp-flags" element={<PrivateRoute roles={ERP_FLAG_ROLES}><Navigate to="/audits" replace /></PrivateRoute>} />
              <Route path="admin/self-check-weights" element={<PrivateRoute roles={ADMIN_ROLES}><SelfCheckWeightsPage /></PrivateRoute>} />
              <Route path="change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
              <Route path="help" element={<PrivateRoute><HelpPage /></PrivateRoute>} />
              <Route path="users" element={<PrivateRoute roles={ADMIN_ROLES}><UsersPage /></PrivateRoute>} />
              <Route path="auditor-pool" element={<PrivateRoute roles={AUDITOR_POOL_ROLES}><AuditorPoolPage /></PrivateRoute>} />
              <Route path="auditor-schedule" element={<PrivateRoute roles={MANAGEMENT_ROLES}><AuditorSchedulePage /></PrivateRoute>} />
              <Route path="assignment-notifications" element={<PrivateRoute roles={MANAGEMENT_ROLES}><AuditorAssignmentNotificationsPage /></PrivateRoute>} />
              <Route path="procedure-workflow" element={<PrivateRoute roles={MANAGEMENT_ROLES}><Navigate to="/audits" replace /></PrivateRoute>} />
              <Route path="audit-plans" element={<PrivateRoute roles={ADMIN_ROLES}><AuditPlansPage /></PrivateRoute>} />
              <Route path="audit-programs" element={<PrivateRoute roles={MANAGEMENT_ROLES}><AuditProgramsPage /></PrivateRoute>} />
              <Route path="audit-reports" element={<PrivateRoute roles={MANAGEMENT_ROLES}><AuditReportsPage /></PrivateRoute>} />
              <Route path="corrective-plans" element={<PrivateRoute roles={AUDIT_ROLES}><CorrectivePlansPage /></PrivateRoute>} />
              <Route path="outgoing-emails" element={<PrivateRoute><OutgoingEmailsPage /></PrivateRoute>} />
              <Route path="procedure-control" element={<PrivateRoute roles={MANAGEMENT_ROLES}><Navigate to="/audits" replace /></PrivateRoute>} />
              <Route path="admin/audit-trail" element={<PrivateRoute roles={ADMIN_ROLES}><AdminAuditTrailPage /></PrivateRoute>} />
              <Route path="admin/system-control" element={<PrivateRoute roles={ADMIN_ROLES}><SystemControlPage /></PrivateRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </AuthProvider></TranslationProvider>
  );
}
